import { getQueryParams } from 'pricing-pages-ui-components/utils/queryParamUtils';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logPPIA0002Exposure, usePPIA0002ExperimentEffect } from '../../../utils/experiments/ppia0002';
import { Chat } from '../../utils/chat/Chat';
import { useSetQueryParams } from '../../utils/urls/appQueryParamUtils';

/**
 * Evaluates the PPIA-0002 experiment and wires up the corner-vs-inline chat
 * widget accordingly:
 *
 * - Control (or ineligible, once the experiment has resolved): load the corner
 *   chat widget and ensure no inline embed is active.
 * - Variant: remove any existing corner widget so `EmbeddedChatWidget` can
 *   mount the inline embed on a page-by-page basis.
 *
 * Intended to be called once from the top-level `Routes` component so the
 * corner widget is managed globally, rather than dropping `<ChatLoader />`
 * onto every page.
 */
export const useChatLoader = () => {
  const experiment = usePPIA0002ExperimentEffect();
  const isControl = experiment.isPPIA0002Control;
  const isVariant = experiment.isPPIA0002Variation;
  const isEligible = experiment.isEligibleForPPIA0002;
  const isExperimentLoaded = experiment.isPPIA0002Fetched;
  const location = useLocation();
  const setQueryParams = useSetQueryParams();
  const key = experiment.key;
  const group = experiment.group;
  useEffect(() => {
    if (!isEligible) {
      setQueryParams({
        [key]: ''
      });
      return;
    }
    logPPIA0002Exposure();
    const queryParams = getQueryParams();
    if (queryParams[key] !== group) {
      setQueryParams({
        [key]: group
      });
    }
    // Check every time user changes routes
  }, [location.pathname, setQueryParams, isEligible, key, group]);
  useEffect(() => {
    if (isControl || !isEligible && isExperimentLoaded) {
      // Load the corner chat widget and ensure no inline embed is active.
      Chat.removeInlineEmbed().remove().load();
    }
    if (isVariant) {
      // Remove any existing corner widget so the inline embed can take over.
      Chat.remove();
    }
  }, [isEligible, isExperimentLoaded, isControl, isVariant]);
};